import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import LiveClassroomViewer from './LiveClassroomViewer';
import { getLiveSocket } from '../../utils/liveSocket';

const authHeaders = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return { 'x-user-id': String(u?._id || u?.id || '') };
  } catch {
    return { 'x-user-id': '' };
  }
};

/**
 * VIEW-ONLY grid of every class that is live right now (Teachers).
 * Contains no toggle / edit controls. Updates in real time via Socket.IO.
 * `onCountChange(n)` lets the parent know how many streams are running.
 */
export default function TeacherLiveGrid({ onCountChange }) {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (signal) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/livestream/active-streams`, {
        headers: authHeaders(),
        signal,
        timeout: 15000
      });
      const list = Array.isArray(res.data) ? res.data : [];
      setStreams(list);
      setError('');
      if (onCountChange) onCountChange(list.length);
    } catch (err) {
      if (axios.isCancel(err)) return;
      setError('We could not load the live classes. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    const socket = getLiveSocket();
    const refresh = () => load();
    socket.on('liveStreamStarted', refresh);
    socket.on('liveStreamStopped', refresh);
    return () => {
      controller.abort();
      socket.off('liveStreamStarted', refresh);
      socket.off('liveStreamStopped', refresh);
    };
  }, [load]);

  if (selected) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setSelected(null)}
          className="mb-3 w-full rounded-xl bg-white py-3 text-lg font-bold text-slate-900 shadow-md active:scale-95"
        >
          ← All Live Classes
        </button>
        <p className="mb-2 text-xl font-bold text-white">{selected.className}</p>
        <LiveClassroomViewer classId={selected.classId} />
      </div>
    );
  }

  if (loading) {
    return <div className="rounded-2xl bg-white p-6 text-center text-xl font-bold text-slate-700">Loading…</div>;
  }

  if (error) {
    return (
      <div role="alert" className="rounded-2xl bg-white p-6 text-center shadow-md">
        <p className="text-xl font-bold text-slate-900">{error}</p>
        <button
          type="button"
          onClick={() => { setLoading(true); load(); }}
          className="mt-4 w-full rounded-xl bg-blue-600 py-4 text-xl font-bold text-white shadow-md active:scale-95"
        >
          🔄 Try Again
        </button>
      </div>
    );
  }

  if (!streams.length) {
    return (
      <div className="rounded-2xl bg-slate-100 p-8 text-center shadow-md">
        <p className="text-2xl font-bold text-slate-900">No Live Classes Right Now.</p>
        <p className="mt-2 text-lg text-slate-700">You will be alerted here when a class goes live.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white p-4 shadow-md">
      <h2 className="mb-3 text-2xl font-bold text-slate-900">
        🔴 {streams.length} Live {streams.length === 1 ? 'Class' : 'Classes'}
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {streams.map((s) => (
          <button
            key={s.streamId}
            type="button"
            onClick={() => setSelected(s)}
            className="rounded-xl border-2 border-rose-500 bg-rose-50 p-4 text-left shadow-md transition active:scale-95"
          >
            <span className="flex items-center gap-2 text-base font-bold text-rose-700">
              <span className="h-3 w-3 animate-pulse rounded-full bg-rose-600" aria-hidden="true" />
              LIVE
            </span>
            <span className="mt-1 block text-xl font-bold text-slate-900">{s.className}</span>
            <span className="mt-2 block text-lg font-bold text-blue-700">▶ Tap to watch</span>
          </button>
        ))}
      </div>
    </div>
  );
}
